import { Link, NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import logo from "../../../assets/logo.png";
import L from "../../../constants/baysportLabels";
import { isOwner } from "../../../utils/roles";
import { logoutFetch } from "../../../store/authSlice";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import { usePortalHtmlClass } from "../../../hooks/usePortalHtmlClass";
import { ADMIN_PAGE_BG, usePwaChrome } from "../../../utils/pwaThemeColor";
import ownerAPI from "../../../services/ownerApi";
import {
  IconChat,
  IconFinance,
  IconLogout,
  IconMembership,
  IconPlus,
  IconSettings,
  IconVenue,
} from "../../ui/icons";
import clinic from "../../Clinic/clinic.module.css";
import { OwnerDashboardProvider, useOwnerDashboard } from "./OwnerDashboardContext";
import s from "./ownerCabinet.module.css";

const NAV = [
  { to: "/account/clubs", end: true, label: "Клубы", Icon: IconVenue },
  { to: "/account/support", label: "Поддержка", Icon: IconChat },
  { to: "/account/billing", label: "Тариф", Icon: IconMembership },
  { to: "/account/payments", label: "Платежи", Icon: IconFinance },
  { to: "/account/profile", label: "Профиль", Icon: IconSettings },
];

export function billingBadgeClass(billing) {
  if (!billing) return s.badgePending;
  if (billing.is_demo_expired || !billing.is_paid) return s.badgeExpired;
  if (billing.is_trial) return s.badgeTrial;
  if (billing.is_expiring_soon) return s.badgeTrial;
  return s.badgeActive;
}

export function formatRub(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0,
  }).format(n);
}

export function formatDateRu(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function paymentStatusLabel(status) {
  const map = {
    confirmed: "Оплачен",
    pending: "Ожидает",
    rejected: "Отклонён",
    cancelled: "Отменён",
  };
  return map[status] || status;
}

function OwnerNavItems({ mobile, supportBadge = 0 }) {
  return NAV.map((item) => {
    const Icon = item.Icon;
    const showSupportBadge = item.to === "/account/support" && supportBadge > 0;
    const badge = showSupportBadge ? (
      <span className={s.navBadge}>
        {supportBadge > 99 ? "99+" : supportBadge}
      </span>
    ) : null;
    if (mobile) {
      return (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            isActive
              ? `${clinic.clientBottomNavItem} ${clinic.clientBottomNavItemActive}`
              : clinic.clientBottomNavItem
          }
        >
          <span className={s.navIconWrap}>
            <Icon className={clinic.clientBottomNavIcon} />
            {badge}
          </span>
          <span>{item.label}</span>
        </NavLink>
      );
    }
    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.end}
        className={({ isActive }) =>
          isActive
            ? `${clinic.navLink} ${clinic.navLinkActive} ${s.ownerNavLinkActive}`
            : `${clinic.navLink} ${s.ownerNavLink}`
        }
      >
        <Icon className={clinic.navIcon} />
        <span>{item.label}</span>
        {badge}
      </NavLink>
    );
  });
}

function OwnerShellInner() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = useIsMobile(768);
  const { data: dashboardData } = useOwnerDashboard();
  const clubQuota = dashboardData?.club_quota;
  const { isAuth, role, isPlatformAdmin, profile } = useSelector((state) => state.auth);
  const [supportBadge, setSupportBadge] = useState(0);

  usePortalHtmlClass("owner");
  usePwaChrome(ADMIN_PAGE_BG, {
    enabled: isMobile,
    isDark: false,
    immersive: false,
  });

  const loadSupportBadge = useCallback(async () => {
    try {
      const data = await ownerAPI.getSupportUnread();
      setSupportBadge(Number(data.unread) || 0);
    } catch {
      setSupportBadge(0);
    }
  }, []);

  useEffect(() => {
    if (!isAuth || isPlatformAdmin || !isOwner(role)) return undefined;
    loadSupportBadge();
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") loadSupportBadge();
    }, 15000);
    const onActivity = () => loadSupportBadge();
    window.addEventListener("baysport:support-activity", onActivity);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("baysport:support-activity", onActivity);
    };
  }, [isAuth, isPlatformAdmin, role, loadSupportBadge]);

  if (!isAuth || isPlatformAdmin || !isOwner(role)) {
    return <Navigate to="/account/login" replace />;
  }

  const logout = async () => {
    await dispatch(logoutFetch());
    navigate("/account/login", { replace: true });
  };

  const initials = String(profile?.full_name || "?")
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div
      className={`${clinic.shell} ${isMobile ? clinic.shellPatientMobile : ""} ${s.ownerShell}`}
    >
      <aside
        className={`${clinic.sidebar} ${isMobile ? clinic.sidebarClientMobile : ""}`}
      >
        <Link to="/account/clubs" className={clinic.sidebarBrand}>
          <img src={logo} alt="" className={clinic.sidebarLogo} />
          <div className={clinic.sidebarBrandText}>
            <span className={clinic.sidebarBrandName}>{L.productName}</span>
            <span className={clinic.sidebarBrandRole}>Кабинет владельца</span>
          </div>
        </Link>

        {!isMobile ? (
          <nav className={`${clinic.nav} ${s.ownerNav}`}>
            <div className={`${clinic.navSectionLabel} ${s.ownerNavSection}`}>
              Управление
            </div>
            <OwnerNavItems supportBadge={supportBadge} />
            <div className={clinic.navSpacer} />
            {clubQuota?.can_register !== false ? (
              <Link
                to="/registraciya?new=1"
                className={`${clinic.navLink} ${s.ownerNavLink}`}
              >
                <span className={clinic.navIcon} aria-hidden>
                  +
                </span>
                <span>Новый клуб</span>
              </Link>
            ) : null}
            <Link to="/" className={`${clinic.navLink} ${s.ownerNavLink}`}>
              <span className={clinic.navIcon} aria-hidden>
                ↗
              </span>
              <span>На сайт</span>
            </Link>
          </nav>
        ) : null}

        <div
          className={`${clinic.userBar} ${isMobile ? clinic.userBarClientMobile : ""}`}
        >
          <div className={clinic.userAvatar} aria-hidden>
            {initials}
          </div>
          {!isMobile ? (
            <div className={clinic.userInfo}>
              <div className={clinic.userName}>{profile?.full_name || "—"}</div>
              <div className={clinic.userRole}>{profile?.email || ""}</div>
            </div>
          ) : null}
          <button
            type="button"
            className={clinic.logoutBtn}
            onClick={logout}
            title="Выйти"
            aria-label="Выйти"
          >
            <IconLogout className={clinic.navIcon} />
          </button>
        </div>
      </aside>

      <main
        className={`${clinic.main} ${isMobile ? clinic.mainClientMobile : ""} ${s.ownerMain}`}
      >
        {isMobile ? (
          <Outlet />
        ) : (
          <div className={s.desktopContent}>
            <Outlet />
          </div>
        )}
      </main>

      {isMobile ? (
        <nav className={clinic.clientBottomNav} aria-label="Кабинет владельца">
          <OwnerNavItems mobile supportBadge={supportBadge} />
        </nav>
      ) : null}
    </div>
  );
}

export default function OwnerShell() {
  return (
    <OwnerDashboardProvider>
      <OwnerShellInner />
    </OwnerDashboardProvider>
  );
}
